import { colors, icons, intl, ui } from '@beelday/common'
import styled from '@emotion/styled'
import { FC, Ref } from 'react'
import type { MediaStatus } from 'room/common/model/media-status'
import { SkeletonWave } from 'room/common/presentation/skeleton'
import { StageControls } from 'room/common/stage-surface'

/** The waveform (100 px) and its timeline. */
const HEIGHT = 120

const Frame = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	min-width: 80%;
`

/** Wavesurfer draws in here; it is there from the start, and unseen until the file is. */
const Wave = styled.div<{ shown: boolean }>`
	display: flex;
	flex-direction: column;
	align-items: center;
	cursor: pointer;
	min-width: 100%;
	min-height: ${HEIGHT}px;
	max-height: ${HEIGHT}px;
	visibility: ${({ shown }) => (shown ? 'visible' : 'hidden')};
`

/** Where the waveform will be, said in words for whoever is looking at it. */
const State = styled.div`
	position: absolute;
	inset: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 12px;
`

const SkeletonShape = styled.div`
	width: 100%;
	height: 64px;
`

const Caption = styled.div`
	font-size: 18px;
	line-height: 24px;
	font-weight: 500;
	text-align: center;
	color: ${colors.brightBlackish};
`

const Missing = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 44px;
	height: 44px;
	border-radius: 50%;
	background-color: ${colors.heather10};
	color: ${colors.heather};

	svg {
		width: 22px;
		height: 22px;
	}

	/* The note, struck through: this recording is not here. */
	&::after {
		content: '';
		position: absolute;
		width: 30px;
		height: 2px;
		border-radius: 1px;
		background-color: currentColor;
		transform: rotate(-45deg);
	}
`

type Props = {
	status: MediaStatus
	/** What wavesurfer draws into. */
	waveRef: Ref<HTMLDivElement>
	onRetry: () => void
}

/**
 * Where a recording's waveform goes, and the truth about it: a calm skeleton
 * while the file is on its way, a quiet note when it is not coming, the
 * waveform when it is there. It says only that - no cause, no file name -
 * since the room may be looking at it on the projector; the button to try
 * again is a stage control, so it is for whoever works the tool.
 */
export const AudioWaveFrame: FC<Props> = ({ status, waveRef, onRetry }) => (
	<Frame
		aria-busy={status === 'loading'}
		data-testid="audio_stream.wave"
		data-status={status}
	>
		<Wave ref={waveRef} shown={status === 'ready'} />
		{status === 'loading' ? (
			<State role="status" data-testid="audio_stream.state.loading">
				<SkeletonShape>
					<SkeletonWave />
				</SkeletonShape>
				<Caption>
					<intl.Translate>audio_stream.load.loading</intl.Translate>
				</Caption>
			</State>
		) : null}
		{status === 'failed' ? (
			<State role="status" data-testid="audio_stream.state.failed">
				<Missing aria-hidden="true">
					<icons.AudioStreamIcon />
				</Missing>
				<Caption>
					<intl.Translate>audio_stream.load.failed</intl.Translate>
				</Caption>
				<StageControls>
					<ui.Button
						small
						type="button"
						color="indigoBlue"
						backgroundColor="white"
						borderColor="indigoBlue"
						onClick={onRetry}
						data-testid="audio_stream.retry"
					>
						<intl.Translate>audio_stream.load.retry</intl.Translate>
					</ui.Button>
				</StageControls>
			</State>
		) : null}
	</Frame>
)
